/**
 * PublicTranscription page - Deepgram Nova-3 streaming transcription with
 * deterministic command detection, designed to be embedded in an iframe
 * from external applications (parallel to PublicRecording).
 *
 * Key differences from PublicRecording:
 *  - No live transcript is shown to the user.
 *  - Final transcripts accumulate in memory inside PublicTranscriptionService.
 *  - When the deterministic matcher fires on "access <known section>", the
 *    page posts a `commandDetected` message to window.parent so the host
 *    app (e.g. EyePegasus EHR) can navigate / react.
 *  - On stop, the full transcript + command log is posted via
 *    `transcriptionComplete`.
 *
 * The URL contract, init validation, and UI chrome are intentionally
 * identical to PublicRecording — only the underlying service (audio buffer
 * → live transcription) is swapped.
 */

import { PublicTranscriptionService } from '@/lib/public-transcription-service';
import { aiScribeService } from '@/lib/ai-scribe-service';
import { envConfig } from '@/lib/env-config';
import { speakText, type SpeakHandle } from '@/lib/text-to-speech';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Types — reuse the existing URL-params contract
import type { PublicRecordingParams } from '@/types/public-recording';

// Hooks
import { usePublicRecordingInit } from '@/hooks/use-public-recording-init';
import { usePublicTranscriptionHandlers } from '@/hooks/use-public-transcription-handlers';
import { usePublicTranscriptionWaveform } from '@/hooks/use-public-transcription-waveform';

// UI components — reused from PublicRecording for visual parity
import { CompactErrorState } from '@/components/PublicRecording/CompactErrorState';
import { CompactListeningIndicator } from '@/components/PublicRecording/CompactListeningIndicator';
import { CompactLoadingState } from '@/components/PublicRecording/CompactLoadingState';
import { CompactUploadingState } from '@/components/PublicRecording/CompactUploadingState';

interface TranscriptionState {
  isValidating: boolean;
  isUploading: boolean;
  isProcessing: boolean;
  isValidated: boolean;
  scribeJobId: string | null;
  error: string | null;
  isRecording: boolean;
  isPaused: boolean;
  audioLevel: number;
  activeSectionKeyword: string | null;
  isSpeaking: boolean;
  speakingLabel: string | null;
  handleStartRecording: () => void;
  handlePauseRecording: () => void;
  handleResumeRecording: () => void;
  handleStopRecording: () => void;
  handleStopSpeaking: () => void;
}

const TranscriptionUI = ({
  isValidating,
  isUploading,
  isProcessing,
  isValidated,
  scribeJobId,
  error,
  isRecording,
  isPaused,
  audioLevel,
  activeSectionKeyword,
  isSpeaking,
  speakingLabel,
  handleStartRecording,
  handlePauseRecording,
  handleResumeRecording,
  handleStopRecording,
  handleStopSpeaking,
}: TranscriptionState) => {
  const getStateKey = () => {
    if (isValidating) return 'loading';
    if (isUploading) return 'uploading';
    if (isProcessing) return 'processing';
    if (error || !isValidated || !scribeJobId) return 'error';
    return 'listening';
  };

  const stateKey = getStateKey();

  return (
    <div className="flex items-center justify-center h-full">
      <AnimatePresence mode="wait">
        {stateKey === 'loading' && <CompactLoadingState key="loading" />}

        {stateKey === 'uploading' && (
          <CompactUploadingState key="uploading" phase="uploading" />
        )}

        {stateKey === 'processing' && (
          <CompactUploadingState key="processing" phase="processing" />
        )}

        {stateKey === 'error' && (
          <CompactErrorState key="error" error={error} />
        )}

        {stateKey === 'listening' && (
          <motion.div
            key="listening"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="w-full">
            <CompactListeningIndicator
              isRecording={isRecording}
              isPaused={isPaused}
              audioLevel={audioLevel}
              activeSectionKeyword={activeSectionKeyword}
              isSpeaking={isSpeaking}
              speakingLabel={speakingLabel}
              onStartRecording={handleStartRecording}
              onPauseRecording={handlePauseRecording}
              onResumeRecording={handleResumeRecording}
              onStopRecording={handleStopRecording}
              onStopSpeaking={handleStopSpeaking}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PublicTranscriptionReal = ({
  params,
}: {
  params: PublicRecordingParams;
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The voice-driven section state machine was retired in favour of
  // per-test Generate buttons in the EHR. The listening pill no longer
  // shows an active-section badge; this state is kept as a typed
  // const-null so existing prop wiring doesn't need to change, and so
  // re-introducing a section indicator later is a one-line revert.
  const activeSectionKeyword: string | null = null;

  const serviceRef = useRef<PublicTranscriptionService | null>(null);

  // Latest dxList snapshot pushed in by the parent (EyePegasus). The EHR
  // posts `{ type: 'dxList', payload }` immediately before the user dictates
  // an Assessment & Plan section so the /api/scribe/transcribe POST that
  // fires on STOP can carry it. Mirrors the legacy PublicRecording flow.
  // Held in a ref (not state) so updates don't re-render the entire page;
  // the section-completed handler reads it lazily at upload time.
  const latestDxListRef = useRef<unknown>(null);

  // Speaking (TTS) state — set while Deepgram Aura plays back a history
  // chunk requested by the parent via `{ type: 'speakText' }`. The
  // listening pill switches to a "Speaking…" affordance while true.
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingLabel, setSpeakingLabel] = useState<string | null>(null);
  const speakHandleRef = useRef<SpeakHandle | null>(null);
  // Stale-closure fix: the postMessage listener that runs
  // beginSpeakHistory() lives inside a useEffect with `[]` deps, so it
  // captures whatever `gatewayToken` was at mount (undefined). The
  // token arrives later from usePublicRecordingInit. Mirroring the
  // state into a ref means the closure always reads the freshest token
  // at SPEAK-command time, not the stale one.
  const gatewayTokenRef = useRef<string | null>(null);
  // Track whether we paused STT specifically for TTS playback so we know
  // whether to resume it on speech-complete. The user could also have
  // paused manually before TTS started — in that case we leave the pause
  // alone.
  const pausedForSpeakRef = useRef(false);

  // Reuse the same validation pipeline as PublicRecording — same URL
  // parameters and same scribe-job contract. We also need the gateway token
  // here because each STOP command triggers a transcript upload to
  // /api/scribe/transcribe and that endpoint requires Bearer auth.
  const {
    isValidated,
    isValidating,
    scribeJobId,
    gatewayToken,
    error: initError,
  } = usePublicRecordingInit(params);

  // Keep the ref pointed at the latest token so the postMessage
  // listener's closure (created once at mount with `[]` deps) can read
  // a fresh value when SPEAK fires later.
  useEffect(() => {
    gatewayTokenRef.current = gatewayToken || null;
  }, [gatewayToken]);

  const { audioLevel } = usePublicTranscriptionWaveform(
    serviceRef,
    isRecording,
    isPaused
  );

  /**
   * Interrupt an in-flight TTS playback WITHOUT ending the voice-scribe
   * session. The TTS helper's `stop()` triggers our `onComplete`
   * callback with reason='stopped', which is the same code path the
   * natural finish takes — meaning STT is resumed automatically (if we
   * were the ones who paused it). Safe to call when nothing is
   * playing — it's a no-op.
   */
  const handleStopSpeaking = () => {
    if (speakHandleRef.current) {
      console.log('[PublicTranscription] User interrupted TTS playback');
      speakHandleRef.current.stop();
    }
  };

  const {
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
  } = usePublicTranscriptionHandlers({
    serviceRef,
    scribeJobId,
    sectionTag: params.sectionTag,
    setIsRecording,
    setIsPaused,
    setIsUploading,
    setIsProcessing,
    setError,
  });

  // Instantiate the transcription service once. Failure here = no API key.
  useEffect(() => {
    if (serviceRef.current) return;
    const apiKey = envConfig.deepgram.apiKey;
    if (!apiKey) {
      console.error(
        '[PublicTranscription] VITE_DEEPGRAM_API_KEY is not configured'
      );
      setError(
        'Transcription service is not configured. Missing Deepgram API key.'
      );
      return;
    }
    try {
      serviceRef.current = new PublicTranscriptionService(apiKey);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to init service');
    }
  }, []);

  // Push the upload context into the service as soon as we have a scribe
  // job id. The service uses it to upload each section's audio to
  // Supabase Storage so the signed URL can travel alongside the transcript.
  useEffect(() => {
    if (!serviceRef.current || !scribeJobId) return;
    serviceRef.current.setUploadContext({
      locationId: params.locationId,
      patientId: params.patientId,
      appointmentId: params.appointmentId,
      scribeJobId,
    });
  }, [
    scribeJobId,
    params.locationId,
    params.patientId,
    params.appointmentId,
  ]);

  // Forward every detected command to the parent window. With voice
  // START/STOP removed, the matcher only emits SPEAK commands these
  // days — but we keep the bridge generic so any future command kinds
  // (history read-back, etc.) reach the EHR automatically.
  useEffect(() => {
    const service = serviceRef.current;
    if (!service) return;
    const unsubscribe = service.onCommandDetected((cmd) => {
      console.log('[PublicTranscription] Command detected:', cmd);
      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'commandDetected',
            sectionTag: params.sectionTag,
            payload: {
              source: 'public-transcription',
              kind: cmd.kind,
              keyword: cmd.keyword,
              commandSectionTag: cmd.sectionTag,
              raw: cmd.raw,
              transcriptSnippet: cmd.transcriptSnippet,
              detectedAt: new Date(cmd.detectedAt).toISOString(),
              scribeJobId,
              sectionTag: params.sectionTag,
            },
          },
          '*'
        );
      }
    });
    return unsubscribe;
  }, [scribeJobId, params.sectionTag, isValidated]);

  // Bridge the service's one-shot "first PCM frame sent" signal to a
  // postMessage so the host EHR can enable its Generate Document /
  // per-test Generate buttons only when a click would actually
  // capture audio data. Confirms mic permission was granted, WS is
  // open, audio is flowing.
  useEffect(() => {
    const service = serviceRef.current;
    if (!service) return;
    const unsubscribe = service.onRecordingStarted(() => {
      console.log('[PublicTranscription] Recording is live — notifying parent');
      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'recordingStarted',
            sectionTag: params.sectionTag,
            payload: {
              source: 'public-transcription',
              scribeJobId,
              sectionTag: params.sectionTag,
              timestamp: new Date().toISOString(),
            },
          },
          '*'
        );
      }
    });
    return unsubscribe;
  }, [scribeJobId, params.sectionTag, isValidated]);

  // When a Generate-click on the EHR side fires
  // `service.generateSectionForKeyword(...)`, this listener POSTs the
  // sliced transcript to the gateway and reports the result back to
  // the parent. Empty transcripts are deliberately uploaded (per
  // product call: n8n handles the no-findings case).
  useEffect(() => {
    const service = serviceRef.current;
    if (!service || !scribeJobId) return;

    const unsubscribe = service.onSectionCompleted(async (evt) => {
      console.log('[PublicTranscription] Section completed:', {
        sectionTag: evt.sectionTag,
        keyword: evt.keyword,
        status: evt.status,
        durationSeconds: evt.durationSeconds,
        transcriptionLength: evt.transcription.length,
      });

      try {
        // New architecture: gateway gets the transcription IMMEDIATELY,
        // no audioUrl. The Supabase audio upload runs in parallel inside
        // the service (uploadSectionAudioInBackground) and never blocks
        // this call.
        //
        // Routing: the combined "Access medical" command produces a
        // section with sectionTag='medicalExams' that goes to a DIFFERENT
        // gateway endpoint (/api/scribe/transcribe-medical-exams) and a
        // different n8n workflow. Every other section keeps using the
        // standard /api/scribe/transcribe path.
        const commonRequest = {
          scribeJobId,
          sectionTag: evt.sectionTag,
          createdBy: 'public-transcription',
          partNumber: 1,
          isFinalPart: true,
          durationSeconds: evt.durationSeconds,
          // n8n previously derived these from the audioUrl path; with
          // audioUrl now empty in the gateway call they travel as
          // explicit fields. ai-scribe-service.ts coerces to Number.
          locationId: params.locationId,
          appointmentId: params.appointmentId,
          clientPlatform: params.clientPlatform,
          patientCode: params.patientCode,
          authToken: gatewayToken || undefined,
          transcription: evt.transcription,
          // ICD-10 coding module flag the EyePegasus EHR forwarded
          // via the iframe URL. Flows through to gateway
          // ScribeRequest and then to the n8n workflow that owns this
          // section.
          icd10CodingEnabled: params.icd10CodingEnabled === true,
          // Assessment & Plan needs the patient's current dxList in the
          // payload. ai-scribe-service.ts gates the dxList field on
          // `sectionTag === 'assessmentAndPlan'`, so passing it for
          // other sections is a no-op — but only attaching it here for
          // the AP path keeps the payload small and obvious.
          dxList:
            evt.sectionTag === 'assessmentAndPlan'
              ? latestDxListRef.current
              : undefined,
        };
        const result =
          evt.sectionTag === 'medicalExams'
            ? await aiScribeService.uploadMedicalExamsTranscript(
                commonRequest
              )
            : evt.sectionTag === 'global'
            ? await aiScribeService.uploadGlobalWorkflowTranscript({
                ...commonRequest,
                // Carry the dxList for the encounter-level workflow too —
                // n8n's global workflow may use it when extracting the
                // Assessment & Plan section.
                dxList: latestDxListRef.current,
              })
            : await aiScribeService.uploadSectionAudioUrl('', commonRequest);

        if (window.parent) {
          window.parent.postMessage(
            {
              type: 'sectionTranscriptionUploaded',
              // Echo BOTH the iframe-session tag AND the section's own tag
              // so the host's filter (which keys off the session tag) and
              // any sectionTag-aware listeners both find what they need.
              sectionTag: params.sectionTag,
              payload: {
                source: 'public-transcription',
                sectionTag: evt.sectionTag,
                keyword: evt.keyword,
                status: evt.status,
                durationSeconds: evt.durationSeconds,
                success: result.success,
                error: result.error,
                partId: result.data?.partId,
                // ONLY populated for sectionTag='global' — the
                // gateway passes through n8n's per-section queue
                // list so the EHR can skeleton each matching test
                // card the moment we ack. Stays undefined for
                // per-section uploads (their own handler dispatches
                // the single Processing state on the card they
                // targeted). `n8nResponse` carries the raw n8n body
                // alongside for any downstream consumer that wants
                // to inspect future fields.
                sections: result.data?.sections,
                n8nResponse: result.data?.n8nResponse,
                scribeJobId,
                transcription: evt.transcription,
                audioUrl: evt.audioUrl,
                timestamp: new Date().toISOString(),
              },
            },
            '*'
          );
        }

        if (!result.success) {
          console.error(
            '[PublicTranscription] Section upload failed:',
            result.error
          );
        }
      } catch (err) {
        console.error(
          '[PublicTranscription] Section upload threw:',
          err
        );
      }
    });

    return unsubscribe;
  }, [
    scribeJobId,
    gatewayToken,
    params.clientPlatform,
    params.patientCode,
    isValidated,
  ]);

  // Send greeting + autostart once validation completes. Matches the
  // PublicRecording handshake so parents that already listen for `greeting`
  // (see EyePegasus ehr-assessment-plan / main-test handlers) work
  // unchanged.
  useEffect(() => {
    if (
      isValidated &&
      scribeJobId &&
      !isValidating &&
      !initError &&
      !error &&
      serviceRef.current
    ) {
      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'greeting',
            payload: {
              message: 'EyeVantage Public Transcription is ready',
              scribeJobId,
              sectionTag: params.sectionTag,
              timestamp: new Date().toISOString(),
              source: 'public-transcription',
            },
          },
          '*'
        );
        handleStartRecording();
      }
    }
  }, [
    isValidated,
    scribeJobId,
    isValidating,
    initError,
    error,
    params.sectionTag,
    handleStartRecording,
  ]);

  // Sync initialization error into local error state.
  useEffect(() => {
    if (initError) setError(initError);
  }, [initError]);

  // Tear down the service when the page unmounts so the mic & socket release.
  useEffect(() => {
    console.log(
      '[PublicTranscription] 📦 PublicTranscriptionReal MOUNTED'
    );

    // === Lifecycle diagnostics =========================================
    // Independent 2-second heartbeat. Fires regardless of audio. If this
    // STOPS appearing while heartbeats from the service also stop, the
    // iframe document itself was destroyed. If this KEEPS appearing while
    // audio heartbeats stop, the script-processor stalled but the page is
    // alive.
    const aliveTimer = window.setInterval(() => {
      console.log(
        '[PublicTranscription] 💓 page-alive',
        new Date().toISOString(),
        {
          documentHidden: document.hidden,
          visibilityState: document.visibilityState,
          hasFocus: document.hasFocus(),
        }
      );
    }, 2000);

    // Parent-to-iframe channel:
    //   - `{ type: 'dxList', payload }` — EyePegasus pushes the patient's
    //     current diagnosis list ahead of the next AP Generate; cached
    //     in latestDxListRef and read at upload time.
    //   - `{ type: 'request-generate-section', payload: {sectionTag, keyword} }`
    //     — Generate-button click from the EHR. Service slices
    //     transcript + audio since the prior Generate cursor and
    //     ships it as that section's payload.
    //   - `{ type: 'speakText', payload: { text, label? } }` — EHR built
    //     a history read-back string and wants the iframe to play it
    //     through Deepgram Aura. We pause STT for the duration so the
    //     spoken audio doesn't loop back through the mic; resume on end.
    //   - `{ type: 'stopSpeaking' }` — abort any in-flight TTS playback
    //     (e.g. the user closed the encounter or started a new command).
    const onParentMessage = (event: MessageEvent) => {
      if (event?.data?.type === 'dxList') {
        latestDxListRef.current = event.data.payload;
        console.log(
          '[PublicTranscription] dxList received from parent',
          Array.isArray(event.data.payload)
            ? `(${event.data.payload.length} item(s))`
            : '(non-array payload)'
        );
        return;
      }
      if (event?.data?.type === 'request-generate-section') {
        // Generate-button click from the EHR side. The service flushes
        // any pending Deepgram finals (so the user's final sentence
        // doesn't bleed into the NEXT section), then slices transcript
        // + audio captured since the last Generate cursor and emits a
        // sectionCompleted event. Empty payloads are intentionally
        // allowed — n8n handles them.
        //
        // The flush makes generateSectionForKeyword async with ~600-
        // 1500ms of perceived latency between click and upload. We
        // fire-and-forget — the message handler returns immediately so
        // the iframe stays responsive to parallel messages (e.g. a
        // dxList push that arrived right before this click).
        const sectionTag: string = event.data?.payload?.sectionTag || '';
        const keyword: string = event.data?.payload?.keyword || '';
        console.log(
          '[PublicTranscription] Generate request from parent:',
          event.data?.source || 'unknown',
          { sectionTag, keyword }
        );
        if (sectionTag) {
          void serviceRef.current?.generateSectionForKeyword(sectionTag, keyword);
        }
        return;
      }
      if (event?.data?.type === 'request-global-workflow') {
        // Encounter-level "Generate Document" click. Slices the FULL
        // transcript captured since the last global Generate (or since
        // recording start) and ships it via the new
        // /api/scribe/global-workflow endpoint — n8n's global workflow
        // fans out per-section extraction.
        //
        // Independent of per-section Generate cursors: this does NOT
        // disturb in-progress per-section work.
        console.log('[PublicTranscription] Global workflow request from parent');
        void serviceRef.current?.generateGlobalWorkflow();
        return;
      }
      if (event?.data?.type === 'speakText') {
        const payload = event.data?.payload || {};
        const text: string = payload.text || '';
        const label: string | null = payload.label || null;
        if (!text.trim()) {
          console.warn(
            '[PublicTranscription] speakText received with empty text — ignoring'
          );
          return;
        }
        void beginSpeak(text, label);
        return;
      }
      if (event?.data?.type === 'speakHistory') {
        // Raw clinical findings → gateway LLM call → narrative → TTS.
        // The EHR ships the per-section data (external slit-lamp +
        // internal ophthalmoscopy fields) and a `target` of which
        // section to read; we resolve the narrative server-side then
        // pipe it through the same beginSpeak() pipeline.
        const payload = event.data?.payload || {};
        void beginSpeakHistory(payload);
        return;
      }
      if (event?.data?.type === 'stopSpeaking') {
        speakHandleRef.current?.stop();
        return;
      }
    };
    window.addEventListener('message', onParentMessage);

    /**
     * Resolve a raw-findings payload into TTS narrative via the gateway,
     * then play it. Mirrors beginSpeak() but slots an LLM call in front.
     * Shows a "Generating…" label while the gateway call is in flight
     * so the clinician doesn't think the system froze.
     */
    async function beginSpeakHistory(payload: any): Promise<void> {
      const target: 'external' | 'internal' | undefined = payload.target;
      const label: string | null = payload.label || null;
      // Start the speaking-state UI immediately with a "Generating…"
      // placeholder. Pauses STT, plays the speaker-icon affordance.
      if (speakHandleRef.current) {
        speakHandleRef.current.stop();
        speakHandleRef.current = null;
      }
      const svc = serviceRef.current;
      pausedForSpeakRef.current = false;
      if (svc) {
        const state = svc.getRecordingState();
        if (state.isRecording && !state.isPaused) {
          try {
            await svc.pauseRecording();
            pausedForSpeakRef.current = true;
          } catch (err) {
            console.warn(
              '[PublicTranscription] pauseRecording before LLM call failed',
              err
            );
          }
        }
      }
      setIsSpeaking(true);
      setSpeakingLabel('Generating summary…');
      notifyParent('speakStarted', { label, stage: 'generating' });

      let narrative = '';
      try {
        const result = await aiScribeService.generateClinicalNarrative(
          {
            external: payload.external ?? null,
            internal: payload.internal ?? null,
            refraction: payload.refraction ?? null,
            chief_complaint: payload.chief_complaint ?? null,
            encounter_id: payload.encounter_id ?? null,
            audience: payload.audience || 'clinician',
            output_mode: 'per_section',
          },
          // Read from the ref — see gatewayTokenRef comment up top.
          // The state value `gatewayToken` here would be the stale
          // mount-time value (usually undefined).
          gatewayTokenRef.current || undefined
        );
        if (!result.success || !result.data) {
          throw new Error(result.error || 'Empty narrative response');
        }
        if (target && result.data.per_section_narrative?.[target]) {
          narrative = result.data.per_section_narrative[target] || '';
        } else {
          narrative = result.data.narrative || '';
        }
        narrative = narrative.trim();
        if (!narrative) {
          narrative =
            target === 'external'
              ? 'No notable external findings to report from the last encounter.'
              : target === 'internal'
              ? 'No notable internal findings to report from the last encounter.'
              : 'No notable findings to report from the last encounter.';
        }
      } catch (err) {
        console.error('[PublicTranscription] LLM narrative failed:', err);
        narrative =
          'Unable to generate the clinical summary right now. Please try again in a moment.';
        notifyParent('speakError', {
          message: err instanceof Error ? err.message : String(err),
          stage: 'narrative',
        });
      }

      // Swap the placeholder label for the real one, then hand off to
      // the TTS pipeline. Reuse beginSpeak() so the cleanup path
      // (resume STT, fire speakComplete) is identical.
      setSpeakingLabel(label);
      // beginSpeak handles its own pause logic; tell it NOT to pause
      // again since we already did. We do this by NOT calling
      // pauseRecording inside beginSpeak — but our beginSpeak does
      // pause unconditionally. Cleanest: replicate the play call here.
      notifyParent('speakStarted', { label, stage: 'speaking' });
      const handle = speakText(narrative, {
        onAudioStart: () => {
          console.log('[PublicTranscription] TTS first audio audible');
        },
        onError: (e) => {
          console.error('[PublicTranscription] TTS error', e);
          notifyParent('speakError', { message: e.message, stage: 'tts' });
        },
        onComplete: (reason) => {
          speakHandleRef.current = null;
          setIsSpeaking(false);
          setSpeakingLabel(null);
          if (pausedForSpeakRef.current && serviceRef.current) {
            pausedForSpeakRef.current = false;
            void serviceRef.current.resumeRecording().catch((e) => {
              console.warn(
                '[PublicTranscription] resumeRecording after TTS failed',
                e
              );
            });
          }
          notifyParent('speakComplete', { reason });
        },
      });
      speakHandleRef.current = handle;
    }

    // Helper that owns the speak lifecycle. Defined inside the effect so
    // it can close over the current serviceRef / setters; doesn't need
    // to be stable across renders.
    async function beginSpeak(
      text: string,
      label: string | null
    ): Promise<void> {
      // Cancel any prior speak before starting a new one — the user
      // might have stacked two SPEAK commands quickly.
      if (speakHandleRef.current) {
        speakHandleRef.current.stop();
        speakHandleRef.current = null;
      }
      const svc = serviceRef.current;
      // Pause STT only if it's actively recording and not already paused
      // (manual pause should be left alone).
      pausedForSpeakRef.current = false;
      if (svc) {
        const state = svc.getRecordingState();
        if (state.isRecording && !state.isPaused) {
          try {
            await svc.pauseRecording();
            pausedForSpeakRef.current = true;
          } catch (err) {
            console.warn(
              '[PublicTranscription] pauseRecording before TTS failed',
              err
            );
          }
        }
      }
      setIsSpeaking(true);
      setSpeakingLabel(label);
      notifyParent('speakStarted', { label });
      const handle = speakText(text, {
        onAudioStart: () => {
          console.log('[PublicTranscription] TTS first audio audible');
        },
        onError: (err) => {
          console.error('[PublicTranscription] TTS error', err);
          notifyParent('speakError', { message: err.message });
        },
        onComplete: (reason) => {
          speakHandleRef.current = null;
          setIsSpeaking(false);
          setSpeakingLabel(null);
          // Resume STT only if we were the ones who paused it.
          if (pausedForSpeakRef.current && serviceRef.current) {
            pausedForSpeakRef.current = false;
            void serviceRef.current.resumeRecording().catch((err) => {
              console.warn(
                '[PublicTranscription] resumeRecording after TTS failed',
                err
              );
            });
          }
          notifyParent('speakComplete', { reason });
        },
      });
      speakHandleRef.current = handle;
    }

    function notifyParent(type: string, payload: Record<string, unknown>): void {
      if (!window.parent) return;
      window.parent.postMessage(
        {
          type,
          sectionTag: params.sectionTag,
          payload: {
            source: 'public-transcription',
            ...payload,
          },
        },
        '*'
      );
    }

    // beforeunload fires when the iframe document is about to be unloaded
    // (Angular destroying the iframe element, src change, parent
    // navigating away). If this fires shortly after a voice command, we
    // know the iframe is being killed by the host.
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      console.error(
        '[PublicTranscription] 🛑 beforeunload — iframe document is being unloaded',
        { wallTime: new Date().toISOString(), event: e }
      );
    };
    const onPageHide = (e: PageTransitionEvent) => {
      console.error(
        '[PublicTranscription] 🛑 pagehide — iframe document hidden/unloaded',
        { persisted: e.persisted, wallTime: new Date().toISOString() }
      );
    };
    const onUnload = () => {
      console.error(
        '[PublicTranscription] 🛑 unload — iframe document fully unloaded'
      );
    };
    const onVisibilityChange = () => {
      console.warn(
        '[PublicTranscription] 👁️ visibilitychange',
        document.visibilityState
      );
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('unload', onUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      console.warn(
        '[PublicTranscription] 📦 PublicTranscriptionReal UNMOUNTING — stopping recording'
      );
      window.clearInterval(aliveTimer);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('unload', onUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('message', onParentMessage);
      // Abort any in-flight TTS so playback doesn't outlive the page.
      if (speakHandleRef.current) {
        speakHandleRef.current.stop();
        speakHandleRef.current = null;
      }
      if (serviceRef.current) {
        const state = serviceRef.current.getRecordingState();
        if (state.isRecording) {
          serviceRef.current.stopRecording().catch(() => {
            /* best effort */
          });
        }
        serviceRef.current = null;
      }
    };
  }, []);

  return (
    <TranscriptionUI
      isValidating={isValidating}
      isUploading={isUploading}
      isProcessing={isProcessing}
      isValidated={isValidated}
      scribeJobId={scribeJobId}
      error={error}
      isRecording={isRecording}
      isPaused={isPaused}
      audioLevel={audioLevel}
      activeSectionKeyword={activeSectionKeyword}
      isSpeaking={isSpeaking}
      speakingLabel={speakingLabel}
      handleStartRecording={handleStartRecording}
      handlePauseRecording={handlePauseRecording}
      handleResumeRecording={handleResumeRecording}
      handleStopRecording={handleStopRecording}
      handleStopSpeaking={handleStopSpeaking}
    />
  );
};

const PublicTranscription = () => {
  const [searchParams] = useSearchParams();

  const params: PublicRecordingParams = {
    appointmentId: searchParams.get('appointmentId') || '',
    patientId: searchParams.get('patientId') || '',
    locationId: searchParams.get('locationId') || '',
    sectionTag: searchParams.get('sectionTag') || '',
    integrationKey: searchParams.get('integrationKey') || '',
    partnerName: searchParams.get('partnerName') || '',
    debugMode: searchParams.get('debug') === 'true',
    patientFirstName: searchParams.get('patientFirstName') || undefined,
    patientLastName: searchParams.get('patientLastName') || undefined,
    patientDob: searchParams.get('patientDob') || undefined,
    clientPlatform: searchParams.get('clientPlatform') || undefined,
    patientCode: searchParams.get('patientCode') || '',
    // ICD-10 coding module flag — case-insensitive 'true' wins;
    // anything else (including missing) is false. Keeps the URL
    // param robust against SSO server casings. Name matches what
    // n8n's workflow expects all the way through.
    icd10CodingEnabled:
      (searchParams.get('icd10CodingEnabled') || '').toLowerCase() === 'true',
  };

  // Transparent body for iframe embedding (same convention as PublicRecording)
  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    return () => {
      document.body.style.backgroundColor = prevBg;
      document.documentElement.style.backgroundColor = prevBg;
    };
  }, []);

  return <PublicTranscriptionReal params={params} />;
};

export default PublicTranscription;
